import { notUndefined } from '@thinkalpha/common/util/notNullOrUndefined.js';
import type { ConcreteIndicator, DictionaryBackedData } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type {
    OrderEntryTicker,
    ToolbarItem,
    NewToolbarItem,
} from '@thinkalpha/platform-ws-client/contracts/user-preference.js';
import type { AlphaEyeSettings } from '@thinkalpha/platform-ws-client/contracts/widget/alpha-eye.js';
import type { TemplateColumnBase } from '@thinkalpha/ui-types/contracts/column-template';
import type { IndicatorFormulaViewModel } from '@thinkalpha/ui-types/contracts/dictionary-view-model';
import type { AlphaLensContent } from '@thinkalpha/ui-types/contracts/workspace';
import { api } from 'src/api';
import { IndicatorImportModel } from 'src/dtos/IndicatorImport';
import { indicatorFromRefLoader } from 'src/lib/dictionary/dictionary';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'api-user-preferences' });

export function getAlphaLensDefaultWidget(): Promise<AlphaLensContent> {
    return api.get(`/user-preferences/alpha-lens-default`).then((res) => res.data);
}

export function getAlphaEyeSettings(): Promise<AlphaEyeSettings> {
    return api.get(`/user-preferences/alpha-eye-settings`).then((res) => res.data);
}

export function getToolbarItems(): Promise<ToolbarItem[]> {
    return api.get(`/user-preferences/toolbar/items`).then((res) => res.data);
}

export function updateToolbarItemsList({ payload }: { payload: NewToolbarItem[] }) {
    return api.post(`/user-preferences/toolbar/items`, payload);
}

// workspace
export async function setDefaultWorkspaceId(workspaceId: string): Promise<string> {
    const response = (
        await api.post(
            `/user-preferences/default-workspace`,
            { workspaceId },
            {
                transformResponse: [(data) => data],
            },
        )
    ).data;

    if (typeof response === 'string') {
        return response;
    }

    return response;
}

// Order Entry
export function getOrderEntryTickers(): Promise<OrderEntryTicker[]> {
    return api.get(`/user-preferences/order-entry/tickers`).then((res) => res.data);
}

export async function getPinnedUniverses(): Promise<string[]> {
    return (await api.get(`/user-preferences/pinned-universes`)).data;
}

export async function getSuggestedColumns(key: string): Promise<TemplateColumnBase[]> {
    return (await api.get(`/user-preferences/suggested-columns`, { params: { key } })).data;
}

export async function getAlphaScansTabIds(): Promise<string[]> {
    return (await api.get(`/user-preferences/alpha-scans-tab-ids`)).data;
}

export async function getDefaultScannerPlanId(): Promise<string> {
    // API returns an un-quoted UUID
    // we don't want AXIOS automatically parsing it as JSON, because it will go boom
    const response = (
        await api.get(`/user-preferences/default-scanner-plan-id`, {
            transformResponse: [(data) => data],
        })
    ).data;

    if (typeof response === 'string') {
        return response;
    }

    log.warn('Default scanner plan ID is not set or returned null from API');
    return '';
}

export async function getIndicatorFormulasFromToolbarItems(
    items: DictionaryBackedData[],
): Promise<IndicatorFormulaViewModel<IndicatorImportModel>[]> {
    const refsToFetch = items.flatMap((current) => {
        if (current.type === 'indicator') {
            return [current.indicator];
        } else {
            return current.imports;
        }
    });

    const results = await indicatorFromRefLoader.loadMany(refsToFetch);

    const indicatorMap = new Map<string, ConcreteIndicator>();
    for (let i = 0; i < refsToFetch.length; i++) {
        const result = results[i];
        if (!(result instanceof Error)) {
            indicatorMap.set(refsToFetch[i].id, result);
        }
    }

    const formulaViewModels: IndicatorFormulaViewModel<IndicatorImportModel>[] = [];

    for (const item of items) {
        if (item.type === 'indicator') {
            const indicator = indicatorMap.get(item.indicator.id);
            if (!indicator) {
                throw new Error(`Indicator with id ${item.indicator.id} not found`);
            }

            formulaViewModels.push({
                formula: indicator.symbol.concat('()'),
                imports: [IndicatorImportModel.fromIndicator(indicator, indicator.symbol)],
            });
        } else if (item.type === 'formula') {
            formulaViewModels.push({
                formula: item.formula,
                imports: item.imports
                    .map((indicatorImport) => {
                        const indicator = indicatorMap.get(indicatorImport.id);
                        if (!indicator) {
                            log.error(`Indicator with id ${indicatorImport.id} not found`);
                            return undefined;
                        }

                        return IndicatorImportModel.fromIndicator(indicator, indicatorImport.alias);
                    })
                    .filter(notUndefined),
            });
        }
    }

    return formulaViewModels;
}
